import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import Sidebar from '@/components/Sidebar';
import AssessmentCard, { Assessment } from '@/components/AssessmentCard';
import { Plus, Search, Filter, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Extend the Assessment interface with skill_questions and voice settings
export interface ExtendedAssessment extends Assessment {
  custom_questions?: string[];
  skill_questions?: Record<string, string[]>;
  language?: string;
  accent?: string;
  voice_id?: string;
}

const Assessments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assessments, setAssessments] = useState<ExtendedAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasActiveIntegration, setHasActiveIntegration] = useState(false);
  const lastFetchedRef = useRef<number>(0);
  const REFETCH_THRESHOLD = 30000;
  
  // New state for adding skills
  const [newSkill, setNewSkill] = useState('');
  const [editingAssessmentId, setEditingAssessmentId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    if (user) {
      fetchAssessments();
      checkActiveIntegration();
    } else {
      setLoading(false);
      setError("Please log in to view your assessments");
    }
  }, [user]);
  
  // Refetch data when the tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        const now = Date.now();
        if (now - lastFetchedRef.current > REFETCH_THRESHOLD) {
          console.log('Assessments data is stale, refreshing...');
          fetchAssessments();
          checkActiveIntegration();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]); // Dependency on user
  
  // Function to check if the organization has an active Knit integration
  const checkActiveIntegration = async () => {
    try {
      // First get the user's organization ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user?.id)
        .single();
        
      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        return;
      }
      
      if (!profile?.organization_id) {
        // console.log("User has no organization ID");
        return;
      }
      
      // Check if there's an active integration for this organization
      const { data: integrations, error: integrationsError } = await supabase
        .from('knit_integrations')
        .select('id')
        .eq('organization_id', profile.organization_id)
        .eq('active', true)
        .limit(1);
        
      if (integrationsError) {
        console.error("Error fetching integrations:", integrationsError);
        return;
      }
      
      setHasActiveIntegration(integrations !== null && integrations.length > 0);
      // console.log("Organization has active integration:", hasActiveIntegration);
    } catch (error) {
      console.error("Error checking for active integrations:", error);
    }
  };
  
  const fetchAssessments = async () => {
    //console.log('[Assessments] Starting fetchAssessments...');
    try {
      setLoading(true);
      setError(null);
      
      // console.log("Fetching assessments for user:", user?.id);
      
      // First get all assessments
      let assessmentsData;
      let assessmentsError;
      
      //console.log('[Assessments] Attempting to fetch assessments data...');
      try {
        // First attempt
        const result = await supabase
          .from('assessments')
          .select('*, custom_questions, skill_questions, language, accent, voice_id')
          .eq('user_id', user?.id)
          .order('created_at', { ascending: false });
          
        assessmentsData = result.data;
        assessmentsError = result.error;
        
        // Log the raw assessments data from the database
        //console.log('[Skills Debug] Raw assessments data from DB:', 
        //  assessmentsData?.map(a => ({ 
        //    id: a.id, 
        //    title: a.title, 
        //    skills: a.skills,
        //    skills_type: Array.isArray(a.skills) ? 'array' : typeof a.skills
        //  }))
        //    );
        
        //console.log('[Assessments] Initial assessments fetch completed.', { hasError: !!assessmentsError, data: assessmentsData });
        
        // If we got an unauthorized error, try refreshing the token and retry
        if (assessmentsError && (assessmentsError.message.includes('JWT expired') || assessmentsError.code === '401')) {
          //console.log('[Assessments] Token expired, refreshing and retrying assessments fetch...');
          
          // Refresh the session token
          const { data: { session: refreshedSession }, error: refreshError } = 
            await supabase.auth.refreshSession();
            
          if (!refreshError && refreshedSession) {
            //console.log('[Assessments] Token refreshed, retrying assessments fetch...');
            // Retry the request after token refresh
            const retryResult = await supabase
              .from('assessments')
              .select('*, custom_questions, skill_questions, language, accent, voice_id')
              .eq('user_id', user?.id)
              .order('created_at', { ascending: false });
              
            assessmentsData = retryResult.data;
            assessmentsError = retryResult.error;
            //console.log('[Assessments] Retry assessments fetch completed.', { hasError: !!assessmentsError, data: assessmentsData });
          } else {
            //console.error('[Assessments] Failed to refresh token.', refreshError);
          }
        }
      } catch (err) {
        //console.error("[Assessments] Error during initial assessments fetch block:", err);
        assessmentsError = err;
      }
      
      if (assessmentsError) {
        //console.error("[Assessments] Final assessments fetch error after potential retry:", assessmentsError);
        throw assessmentsError;
      }

      // Then get all candidates for these assessments
      const assessmentIds = assessmentsData?.map(a => a.id) || [];
      //console.log(`[Assessments] Fetching candidates for ${assessmentIds.length} assessment(s)...`, assessmentIds);
      
      // TEMP: Comment out candidate fetch for debugging
      // REVERT: Restore candidate fetch
      // /*
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select('*')
        .in('assessment_id', assessmentIds);

      if (candidatesError) {
        console.error("[Assessments] Error fetching candidates:", candidatesError);
        throw candidatesError;
      }
      //console.log(`[Assessments] Successfully fetched ${candidatesData?.length ?? 0} candidates.`);
      // */
     // const candidatesData: any[] = []; // TEMP: Use empty array
     // console.log('[Assessments] TEMP: Skipping candidate fetch, using empty array.');

      // console.log("Assessments data received:", assessmentsData);

      //console.log('[Assessments] Formatting assessments data...');
      const formattedAssessments: ExtendedAssessment[] = (assessmentsData || []).map(assessment => {
        // Get candidates for this assessment
        // TEMP: Use empty array as candidatesData is unavailable
        // REVERT: Use actual candidatesData
        const assessmentCandidates = (candidatesData || []).filter(c => c.assessment_id === assessment.id);
        const completedCount = assessmentCandidates.filter(c => c.status === 'completed').length;
        const inProgressCount = assessmentCandidates.filter(c => c.status === 'in_progress').length;
        const invitedCount = assessmentCandidates.filter(c => c.status === 'invited').length;
        const totalCount = invitedCount + inProgressCount + completedCount; // Total is sum of all candidates

        // Parse skill_questions if it's a string
        let parsedSkillQuestions: Record<string, string[]> = {};
        if (assessment.skill_questions) {
          if (typeof assessment.skill_questions === 'string') {
            try {
              parsedSkillQuestions = JSON.parse(assessment.skill_questions);
            } catch (e) {
              console.error("Error parsing skill_questions:", e);
            }
          } else if (typeof assessment.skill_questions === 'object') {
            parsedSkillQuestions = assessment.skill_questions as Record<string, string[]>;
          }
        }

        // Count total skill-specific questions
        const skillQuestionsCount = Object.values(parsedSkillQuestions).reduce(
          (sum, questions) => sum + (Array.isArray(questions) ? questions.length : 0), 
          0
        );

        return {
          id: assessment.id,
          title: assessment.title,
          skills: assessment.skills || [],
          candidatesInvited: totalCount,
          candidatesCompleted: completedCount,
          candidatesInProgress: inProgressCount,
          date: new Date(assessment.created_at).toLocaleDateString(),
          status: assessment.status,
          source: assessment.source || 'RoundOne',
          ats_job_id: assessment.ats_job_id,
          custom_questions: assessment.custom_questions || [],
          skill_questions: parsedSkillQuestions,
          language: assessment.language,
          accent: assessment.accent,
          voice_id: assessment.voice_id
        };
      });

      //console.log(`[Assessments] Setting assessments state with ${formattedAssessments.length} items.`);
      setAssessments(formattedAssessments);
      
      // Update last fetched timestamp
      lastFetchedRef.current = Date.now();

    } catch (error: any) {
      console.error('[Assessments] Error in fetchAssessments catch block:', error);
      setError(error.message || 'Failed to load assessments');
      toast.error('Failed to load assessments: ' + (error.message || 'Unknown error'));
    } finally {
      //console.log('[Assessments] Setting loading state to false.');
      setLoading(false);
    }
  };
  
  // Function to handle adding a skill to an assessment
  const handleAddSkill = (assessmentId: string) => {
    //console.log('***** HANDLE ADD SKILL TRIGGERED *****', { assessmentId, newSkill });
    
    if (!newSkill.trim()) {
      setEditingAssessmentId(null);
      return;
    }
    
    // Get the assessment
    const assessment = assessments.find(a => a.id === assessmentId);
    if (!assessment) {
      toast.error('Assessment not found');
      return;
    }
    
    // Check if skill already exists
    if (assessment.skills.includes(newSkill)) {
      toast.error('This skill is already added');
      return;
    }
    
    // Add skill to local state first
    const updatedSkills = [...assessment.skills, newSkill];
    //console.log('***** UPDATING SKILLS *****', { 
    //  oldSkills: assessment.skills,
    //  newSkill,
    //  updatedSkills
    //});
    
    // Update local state
    setAssessments(prev => 
      prev.map(a => a.id === assessmentId 
        ? { ...a, skills: updatedSkills } 
        : a
      )
    );
    
    // Save to database
    saveSkillsToDatabase(assessmentId, updatedSkills);
    
    // Reset state
    setNewSkill('');
    setEditingAssessmentId(null);
  };
  
  // Function to actually save the skills to the database
  const saveSkillsToDatabase = async (assessmentId: string, skills: string[]) => {
    setIsSaving(true);
    
    //console.log('[Skills Debug] Starting to save skills:', { assessmentId, skills });
    
    try {
      const { data, error } = await supabase
        .from('assessments')
        .update({ skills })
        .eq('id', assessmentId)
        .select();
      
      if (error) throw error;
      
      //console.log('[Skills Debug] Skills saved successfully:', data);
      toast.success('Skill added successfully');
    } catch (error: any) {
      //console.error('[Skills Debug] Error saving skills:', error);
      toast.error('Failed to save skill: ' + (error.message || 'Unknown error'));
      
      // Revert the local state change on error
      fetchAssessments();
    } finally {
      setIsSaving(false);
    }
  };
  
  // Function to handle removing a skill
  const handleRemoveSkill = (assessmentId: string, skillToRemove: string) => {
    // Get the assessment
    const assessment = assessments.find(a => a.id === assessmentId);
    if (!assessment) {
      toast.error('Assessment not found');
      return;
    }
    
    // Remove skill from local state first
    const updatedSkills = assessment.skills.filter(skill => skill !== skillToRemove);
    
    // Update local state
    setAssessments(prev => 
      prev.map(a => a.id === assessmentId 
        ? { ...a, skills: updatedSkills } 
        : a
      )
    );
    
    // Save to database
    saveSkillsToDatabase(assessmentId, updatedSkills);
  };
  
  const filteredAssessments = assessments.filter(assessment => {
    const matchesSearch = assessment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assessment.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || assessment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleRetry = () => {
    fetchAssessments();
    checkActiveIntegration();
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 animate-fade-in">
            <div>
              <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">Assessments</h1>
              <p className="text-muted-foreground">Manage and track all of your assessments</p>
            </div>
            <div className="flex gap-4 mt-4 sm:mt-0">
              {hasActiveIntegration && (
                <Button 
                  variant="outline"
                  onClick={() => navigate('/ats-jobs')}
                >
                  Show ATS Jobs
                </Button>
              )}
              <Button 
                className="gradient-button" 
                onClick={() => navigate('/assessments/create')}
              >
                <Plus size={16} className="mr-2" /> Create Assessment
              </Button>
            </div>
          </div>
          
          {/* Filters */}
          {!error && (
            <div className="flex flex-col sm:flex-row gap-4 mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search assessments..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Assessment Cards or Error State */}
          {loading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
              <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>Error: {error}</span>
              </div>
              <p className="mb-4 text-muted-foreground">
                There was a problem connecting to the server. Please check your connection and try again.
              </p>
              <Button onClick={handleRetry}>
                Retry
              </Button>
            </div>
          ) : filteredAssessments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {filteredAssessments.map((assessment) => (
                <AssessmentCard
                  key={assessment.id} 
                  assessment={assessment}
                  className="hover:scale-[1.02] transition-transform duration-200"
                  onClick={() => navigate(`/assessments/${assessment.id}`)}
                  onDelete={() => {
                    setAssessments(assessments.filter(a => a.id !== assessment.id));
                    toast.success('Assessment deleted successfully');
                  }}
                  onArchive={() => {
                    setAssessments(assessments.map(a => 
                      a.id === assessment.id 
                        ? { ...a, status: 'archived' as const } 
                        : a
                    ));
                    toast.success('Assessment archived successfully');
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-4xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold mb-2">No assessments found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || statusFilter !== 'all'
                  ? "Try adjusting your filters"
                  : "Create your first assessment to get started"}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button 
                  className="gradient-button" 
                  onClick={() => navigate('/assessments/create')}
                >
                  <Plus size={16} className="mr-2" /> Create Assessment
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Assessments;
